// The rendered datasheet, shared by the composer, the index composer and the package preview.
import { revRows, sectionRows } from '../development/dsTemplateLayout';
import { CALC_LISTS, isDsValues, listNameOf } from '../development/dsCalc';
import bodyRowPairs from '../development/dsBodyRows';
import { FONT, THIN, THICK, ROW_H, base, SECTION_HEADS, CAL_COLS, CAL_HEADS, COMP_COLS, COMP_HEADS, STATIC_SECTIONS, hasStaticContent, getHeaderRow, getCenterBoxes, getBodyLine, getSectionCells, TABLE_W, REV_COLS } from './dsBoxes';
import StuiSelect from '../../components/common/StuiSelect';

// Exported so the ISA Datasheet Package preview renders the SAME canvas rather than a second
// look-alike: a datasheet looks identical whether it is being composed or reviewed for approval.
// The in-sheet entry field. The sheet is paper, so it is pinned light like LIGHT_FIELD, and it is
// borderless so the printed cell border is the only frame.
const USER_INPUT_STYLE = { border: 'none', outline: 'none', background: 'transparent', color: '#222', ...FONT, padding: '0 2px', minWidth: 0 };

//
// Optional props (all default to the plain editable preview):
//   highlight    Map or object of boxId -> 'changed' | 'added' | 'removed'; those boxes' cells
//                are outlined and tinted (removed ones are struck through).
//   readOnly     inputs and dropdowns cannot be edited.
//   onLabelClick called with a label or section box's text when that text is clicked.
export function DSPreview({ boxes, draftValues = {}, setDraftValues, resolveDataValue, sheetCount = 0, layout = null, highlight = null, readOnly = false, onLabelClick = undefined }) {
  const centerList = getCenterBoxes(boxes);
  const sections   = sectionRows(layout) ?? (hasStaticContent(boxes) ? STATIC_SECTIONS : []);

  // The cell treatment for a highlighted box, or null when it is not in the highlight set.
  const highlightStyle = (box) => {
    const kind = highlight instanceof Map ? highlight.get(box.id) : highlight?.[box.id];
    if (!kind) return null;
    return {
      outline: '2px solid var(--color-warning)', outlineOffset: -2, background: 'var(--color-dirty-bg)',
      ...(kind === 'removed' ? { textDecoration: 'line-through' } : {}),
    };
  };

  // A label or section's text is clickable only when the caller supplied a handler.
  const labelClick = (box) => (onLabelClick ? { onClick: () => onLabelClick(box.label), style: { cursor: 'pointer' } } : null);

  // A user-entered box: a text input, or a dropdown when its binding names a list. A value
  // outside the list (typed before the box became a dropdown, or frozen in a snapshot) stays
  // selectable so it is shown rather than silently blanked.
  const userInput = (box, flex) => {
    const style = { ...USER_INPUT_STYLE, ...(flex ? { flex: 1 } : { width: '100%' }) };
    const value = draftValues[box.id] ?? '';
    const onChange = e => { if (!readOnly) setDraftValues(prev => ({ ...prev, [box.id]: e.target.value })); };
    const list = listNameOf(box.fieldSource);
    if (list) {
      const options = CALC_LISTS[list] ?? [];
      return (
        <StuiSelect style={style} value={value} onChange={onChange} disabled={readOnly}>
          <option value="" />
          {options.map(o => <option key={o} value={o}>{o}</option>)}
          {value && !options.includes(value) && <option value={value}>{value}</option>}
        </StuiSelect>
      );
    }
    return <input style={style} value={value} onChange={onChange} readOnly={readOnly} />;
  };

  // Render a box's content: editable input for dsvalues, resolved text otherwise. A calc box
  // is not user-defined: resolveDataValue computes it, so it reads as text and updates as the
  // inputs it depends on are typed.
  // wrapText: true disables nowrap/ellipsis (used for center panel slots)
  const boxContent = (box, wrapText = false) => {
    const isUserDefined = isDsValues(box.fieldSource);
    const clip = wrapText ? { whiteSpace: 'normal', wordBreak: 'break-word' } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
    if (box.type === 'label') {
      const click = labelClick(box);
      return <span {...click} style={{ fontWeight: 600, ...(wrapText ? {} : { fontSize: '8pt' }), ...clip, ...click?.style }}>{box.label}</span>;
    }
    if (box.type === 'section') {
      const click = labelClick(box);
      return <span {...click} style={{ fontWeight: 700, ...(wrapText ? {} : { fontSize: '7pt', textTransform: 'uppercase', letterSpacing: 0.4 }), ...click?.style }}>{box.label}</span>;
    }
    if (box.type === 'value') {
      if (isUserDefined) return userInput(box, false);
      const val = resolveDataValue(box);
      return <span style={{ ...clip, color: val ? undefined : '#bbb' }}>{val || '—'}</span>;
    }
    // label-value
    const val = isUserDefined ? (draftValues[box.id] ?? '') : resolveDataValue(box);
    return (
      <div style={{ display: 'flex', alignItems: wrapText ? 'flex-start' : 'center', width: '100%', minWidth: 0, gap: 3, flexWrap: wrapText ? 'wrap' : undefined }}>
        {box.label && <span style={{ fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, ...(wrapText ? {} : { fontSize: '8pt' }) }}>{box.label}:</span>}
        {isUserDefined ? userInput(box, true) : (
          <span style={{ flex: 1, ...clip, color: val ? undefined : '#bbb' }}>{val || '—'}</span>
        )}
      </div>
    );
  };

  const cellContent = (lineBoxes) => {
    if (lineBoxes.length === 0) return null;
    const hasSection    = lineBoxes.some(b => b.type === 'section');
    const hasUserDefined = lineBoxes.some(b => isDsValues(b.fieldSource));
    const outerPad = (hasSection || hasUserDefined) ? 0 : '0 2px';
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: ROW_H, padding: outerPad }}>
        {lineBoxes.map((box, i) => {
          const isUD = isDsValues(box.fieldSource);
          const bg   = box.type === 'section' ? '#e4e4e4' : isUD ? '#fff59d' : undefined;
          const pad  = (box.type === 'section' || isUD) ? 0 : '0 2px';
          return (
            <div key={box.id} style={{ flexGrow: 0, flexShrink: 0, flexBasis: `${((box.colSpan || 12) / 12) * 100}%`, boxSizing: 'border-box', borderRight: i < lineBoxes.length - 1 ? THIN : 'none', height: '100%', display: 'flex', alignItems: 'center', justifyContent: box.type === 'section' ? 'center' : undefined, padding: pad, minWidth: 0, overflow: 'hidden', background: bg, ...highlightStyle(box) }}>
              {boxContent(box)}
            </div>
          );
        })}
      </div>
    );
  };

  const lineNoStyle  = (extra = {}) => ({ ...base, width: 28, textAlign: 'center', border: THIN, borderLeft: THICK, color: '#555', ...extra });
  const revStyle     = (extra = {}) => ({ ...base, width: 14, textAlign: 'center', border: THIN, color: '#888', ...extra });

  /**
   * Static sections (lines 110-124) render as a NESTED TABLE, not a flex row of fixed widths.
   *
   * A flex row has to sum to exactly whatever the colSpan-4 cell happens to be, and any padding
   * or divider pushes the last column past that cell's bold right border. A nested table at
   * width:100% with tableLayout:fixed fills the cell exactly and the browser enforces the column
   * boundaries, so the verticals line up with the rows above and with the footer BY CONSTRUCTION
   * rather than by matching pixel totals across three tables.
   *
   * Widths are percentages of their own group, so the subheader and its data rows always share
   * the same boundaries whatever the cell's real width turns out to be.
   */
  const sectionTable = (widths, renderCell, cellStyle = {}) => {
    const styleFor = ci => (typeof cellStyle === 'function' ? cellStyle(ci) : cellStyle);
    const totalW = widths.reduce((sum, w) => sum + w, 0);
    return (
      <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%', ...FONT }}>
        <colgroup>
          {widths.map((w, i) => <col key={i} style={{ width: `${(w / totalW) * 100}%` }} />)}
        </colgroup>
        <tbody>
          <tr style={{ height: ROW_H }}>
            {widths.map((w, ci) => (
              <td key={ci} style={{ height: ROW_H, borderRight: ci < widths.length - 1 ? THIN : 'none', padding: 0, overflow: 'hidden', ...styleFor(ci) }}>
                {renderCell(ci)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    );
  };

  // The group header's boundaries are the CAL_COLS boundaries grouped up:
  // 140+150 | 60+60 | 82 | 75+75 — so it aligns with the subheader and data rows below it.
  const CAL_GROUPS = [
    ['CALIBRATIONS AND TEST', CAL_COLS[0] + CAL_COLS[1]],
    ['INPUT OR TEST',         CAL_COLS[2] + CAL_COLS[3]],
    ['ACTION',                CAL_COLS[4]],
    ['OUTPUT OR SCALE',       CAL_COLS[5] + CAL_COLS[6]],
  ];

  const previewSectionRow = (row) => {
    const isLast = row.lineNo === sections[sections.length - 1].lineNo;
    const bBot   = isLast ? THICK : THIN;
    const bTop   = SECTION_HEADS.has(row.type) ? THICK : THIN;   // same rule as the canvas
    const cells  = getSectionCells(boxes, row.lineNo);
    const shellTd = { border: THIN, borderTop: bTop, borderBottom: bBot, borderRight: THICK, padding: 0 };
    const headCell = { background: '#e4e4e4', textAlign: 'center', fontWeight: 600, fontSize: '7pt', padding: '0 2px', verticalAlign: 'middle' };
    let contentTd;

    if (row.type === 'cal-group-header') {
      contentTd = (
        <td colSpan={4} style={shellTd}>
          {sectionTable(CAL_GROUPS.map(g => g[1]), ci => CAL_GROUPS[ci][0], headCell)}
        </td>
      );
    } else if (row.type === 'cal-subheader') {
      contentTd = (
        <td colSpan={4} style={shellTd}>
          {sectionTable(CAL_COLS, ci => CAL_HEADS[ci], headCell)}
        </td>
      );
    } else if (row.type === 'cal-data') {
      contentTd = (
        <td colSpan={4} style={shellTd}>
          {sectionTable(CAL_COLS, ci => {
            const cellBox = cells.find(b => b.order === ci + 1) || null;
            return cellBox ? boxContent(cellBox) : null;
          }, ci => ({ padding: '0 3px', verticalAlign: 'middle', ...highlightStyle(cells.find(b => b.order === ci + 1) || {}) }))}
        </td>
      );
    } else if (row.type === 'comp-header') {
      contentTd = <td colSpan={4} style={{ ...shellTd, ...headCell }}>COMPONENT IDENTIFICATIONS</td>;
    } else if (row.type === 'comp-subheader') {
      contentTd = (
        <td colSpan={4} style={shellTd}>
          {sectionTable(COMP_COLS, ci => COMP_HEADS[ci], headCell)}
        </td>
      );
    } else if (row.type === 'comp-data') {
      contentTd = (
        <td colSpan={4} style={shellTd}>
          {sectionTable(COMP_COLS, ci => {
            const cellBox = cells.find(b => b.order === ci + 1) || null;
            return cellBox ? boxContent(cellBox) : null;
          }, ci => ({ padding: '0 3px', verticalAlign: 'middle', ...highlightStyle(cells.find(b => b.order === ci + 1) || {}) }))}
        </td>
      );
    } else {
      contentTd = <td colSpan={4} style={{ border: THIN, borderTop: bTop, borderBottom: bBot, borderRight: THICK }} />;
    }

    return (
      <tr key={`ps-${row.lineNo}`} style={{ height: ROW_H }}>
        <td style={lineNoStyle({ borderTop: bTop, borderBottom: bBot })}>{row.lineNo}</td>
        <td style={revStyle({ borderTop: bTop, borderBottom: bBot })}>A</td>
        {contentTd}
      </tr>
    );
  };

  return (
    // The sheet is paper: it stays light and its text stays black in every theme — same
    // treatment as the template composer's canvas above. stui-rpt-preview-container pins the
    // --color-* vars to their light values so themed greys cannot darken into the white page,
    // and color:'#222' stops the sheet text following the theme. Its overflow:hidden is
    // re-opened so nothing on the sheet gets clipped.
    <div className="stui-rpt-preview-container"
      style={{ background: '#fff', color: '#222', overflow: 'visible', boxShadow: '0 3px 18px rgba(0,0,0,0.28)', padding: '24px 28px', width: TABLE_W + 56, boxSizing: 'border-box', alignSelf: 'flex-start' }}>

      {/* Header table */}
      <table style={{ borderCollapse: 'collapse', width: TABLE_W, tableLayout: 'fixed', background: '#fff', ...FONT }}>
        <colgroup><col style={{ width: 28 }} /><col style={{ width: 14 }} /><col style={{ width: 186 }} /><col style={{ width: 228 }} /><col style={{ width: 28 }} /><col style={{ width: 14 }} /><col style={{ width: 186 }} /></colgroup>
        <tbody>
          {[0,1,2,3,4].map(i => {
            const lNo = i + 1, rNo = i + 6, isFirst = i === 0, isLast = i === 4;
            const lBoxes = getHeaderRow(boxes, lNo), rBoxes = getHeaderRow(boxes, rNo);
            const bTop = isFirst ? THICK : THIN, bBot = isLast ? THICK : THIN;
            return (
              <tr key={`ph${lNo}`} style={{ height: ROW_H }}>
                <td style={lineNoStyle({ borderTop: bTop, borderBottom: bBot })}>{lNo}</td>
                <td style={revStyle({ borderTop: bTop, borderBottom: bBot })}>A</td>
                <td style={{ ...base, border: THIN, borderTop: bTop, borderBottom: bBot, padding: 0 }}>{cellContent(lBoxes)}</td>
                {isFirst && (
                  <td rowSpan={5} style={{ border: THIN, borderTop: THICK, borderBottom: THICK, padding: 0, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: ROW_H * 5 }}>
                      {[{ slot: 1, flex: 1 }, { slot: 2, flex: 2 }, { slot: 3, flex: 1 }].map(({ slot, flex }) => {
                        const sb = centerList.find(b => (b.order || 1) === slot) || null;
                        return (
                          <div key={slot} style={{ flex, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', boxSizing: 'border-box', textAlign: 'center', wordBreak: 'break-word', overflow: 'visible', fontSize: 14, fontWeight: 700, ...(sb ? highlightStyle(sb) : null) }}>
                            {sb ? boxContent(sb, true) : null}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                )}
                <td style={lineNoStyle({ borderTop: bTop, borderBottom: bBot })}>{rNo}</td>
                <td style={revStyle({ borderTop: bTop, borderBottom: bBot })}>A</td>
                <td style={{ ...base, border: THIN, borderTop: bTop, borderBottom: bBot, borderRight: THICK, padding: 0 }}>{cellContent(rBoxes)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Body table */}
      <table style={{ borderCollapse: 'collapse', width: TABLE_W, tableLayout: 'fixed', background: '#fff', ...FONT }}>
        <colgroup><col style={{ width: 28 }} /><col style={{ width: 14 }} /><col style={{ width: 300 }} /><col style={{ width: 28 }} /><col style={{ width: 14 }} /><col style={{ width: 300 }} /></colgroup>
        <tbody>
          {bodyRowPairs(boxes, sheetCount, layout).map(({ leftLine: lLine, rightLine: rLine }, i, arr) => {
            const isLast = i === arr.length - 1;
            const bBot = isLast ? THICK : THIN;
            return (
              <tr key={i} style={{ height: ROW_H }}>
                <td style={lineNoStyle({ borderTop: 'none', borderBottom: bBot })}>{lLine ?? ''}</td>
                <td style={revStyle({ borderTop: 'none', borderBottom: bBot })}>{lLine == null ? '' : 'A'}</td>
                <td style={{ ...base, border: THIN, borderTop: 'none', borderBottom: bBot, padding: 0 }}>{lLine == null ? null : cellContent(getBodyLine(boxes, lLine))}</td>
                <td style={lineNoStyle({ borderTop: 'none', borderBottom: bBot })}>{rLine ?? ''}</td>
                <td style={revStyle({ borderTop: 'none', borderBottom: bBot })}>{rLine == null ? '' : 'A'}</td>
                <td style={{ ...base, border: THIN, borderTop: 'none', borderBottom: bBot, borderRight: THICK, padding: 0 }}>{rLine == null ? null : cellContent(getBodyLine(boxes, rLine))}</td>
              </tr>
            );
          })}
          {sections.map(row => previewSectionRow(row))}
        </tbody>
      </table>

      {/* Revision footer */}
      <table style={{ borderCollapse: 'collapse', width: TABLE_W, ...FONT, borderTop: THICK }}>
        <colgroup>{REV_COLS.map((c, i) => <col key={i} style={c.width ? { width: c.width } : {}} />)}</colgroup>
        <tbody>
          <tr style={{ background: '#f0f0f0' }}>
            {REV_COLS.map((c, ci) => (
              <td key={ci} style={{ ...base, height: 'auto', lineHeight: '14px', padding: '2px 3px', textAlign: 'center', fontWeight: 600, fontSize: '7.5pt', whiteSpace: 'normal', border: THIN, borderLeft: ci === 0 ? THICK : THIN, borderRight: ci === REV_COLS.length - 1 ? THICK : THIN }}>
                {Array.isArray(c.label) ? c.label.map((l, li) => <div key={li}>{l}</div>) : c.label}
              </td>
            ))}
          </tr>
          {revRows(layout).map((ri, _, all) => (
            <tr key={ri} style={{ height: 18 }}>
              {REV_COLS.map((_c, ci) => <td key={ci} style={{ height: 18, border: THIN, borderLeft: ci === 0 ? THICK : THIN, borderRight: ci === REV_COLS.length - 1 ? THICK : THIN, borderBottom: ri === all.length - 1 ? THICK : THIN }} />)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
